/**
 * resolveApiBase — auto-healing API base URL resolver.
 *
 * Background (the 2026-06-17 production verify-email bug):
 *   Production verify-email links failed with "Invalid or already-used"
 *   because the production React bundle was built with a stale
 *   REACT_APP_BACKEND_URL pointing at the *preview* backend. The token
 *   was issued by the production backend (a different DB), the click
 *   loaded the production frontend, the frontend called the preview
 *   backend, the preview DB had no matching token, 400.
 *
 * This helper protects against that whole class of bug at runtime:
 *
 *   - If REACT_APP_BACKEND_URL is empty → fall back to same-origin.
 *   - If we're running on a real production host (anything that is NOT
 *     localhost/preview), but the configured BACKEND_URL points at a
 *     preview/localhost URL → prefer same-origin instead. We assume the
 *     page is being served by a host that also routes /api/* to the
 *     intended backend, which is how Emergent's production proxy works.
 *   - Otherwise → return the configured BACKEND_URL verbatim.
 *
 * The helper is intentionally pure and synchronous so it can be used at
 * module top-level (e.g. `const API = resolveApiBase()`).
 *
 * Returns a URL with NO trailing slash. Callers should append `/api/...`.
 */
const PREVIEW_HOST_FRAGMENTS = [
  'preview.emergentagent.com',
  'preview.emergentcf.cloud',
  'localhost',
  '127.0.0.1',
];

function _looksLikePreview(url) {
  if (!url) return true;
  const low = String(url).toLowerCase();
  return PREVIEW_HOST_FRAGMENTS.some((frag) => low.includes(frag));
}

function _looksLikePreview_host(host) {
  if (!host) return true;
  const low = String(host).toLowerCase();
  return PREVIEW_HOST_FRAGMENTS.some((frag) => low.includes(frag));
}

export function resolveApiBase() {
  const configured = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  const sameOrigin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : '';

  // SSR / build-time fallback
  if (!sameOrigin) return configured;

  const currentHost = window.location.hostname;
  const onPreviewHost = _looksLikePreview_host(currentHost);

  // If the bundle was built with NOTHING, always same-origin.
  if (!configured) return sameOrigin;

  // If the page is being served by a real production host but the
  // configured backend looks like preview/localhost — that's the stale-
  // build case. Auto-heal by preferring same-origin.
  if (!onPreviewHost && _looksLikePreview(configured)) {
    return sameOrigin;
  }

  // Otherwise trust the configured value (covers preview→preview and
  // production→production-with-correct-config cases).
  return configured;
}

// Convenience constant for modules that just want `const API = API_BASE`.
export const API_BASE = resolveApiBase();

// Exposed for tests + diagnostic UIs.
export function diagnoseApiBase() {
  const configured = (process.env.REACT_APP_BACKEND_URL || '');
  const sameOrigin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : null;
  const currentHost =
    typeof window !== 'undefined' && window.location
      ? window.location.hostname
      : null;
  const resolved = resolveApiBase();
  return {
    configured,
    configured_looks_like_preview: _looksLikePreview(configured),
    same_origin: sameOrigin,
    current_host: currentHost,
    current_host_looks_like_preview: _looksLikePreview_host(currentHost),
    resolved,
    auto_healed_to_same_origin:
      Boolean(sameOrigin) &&
      configured &&
      !_looksLikePreview_host(currentHost) &&
      _looksLikePreview(configured),
  };
}
